import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Search, Copy, ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { ROLE_LABEL } from "@/hooks/usePermissions";

const inputCls = "w-full border border-zinc-300 px-3 py-2 text-sm rounded-none bg-white focus:outline-none focus:ring-2 focus:ring-[#0056B3] focus:ring-offset-1";
const labelCls = "text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 block mb-1";
const btnPri = "text-xs font-mono uppercase tracking-[0.15em] bg-[#0056B3] text-white px-4 py-2 hover:bg-[#004494] inline-flex items-center gap-1.5";
const btnSec = "text-xs font-mono uppercase tracking-[0.15em] border border-zinc-300 px-3 py-2 hover:bg-zinc-100 inline-flex items-center gap-1.5";

const roleColorCls = {
  admin: "bg-[#0056B3] text-white",
  dispatcher: "bg-emerald-600 text-white",
  biller: "bg-amber-600 text-white",
};

const roleIcons = {
  admin: ShieldCheck,
  dispatcher: ShieldAlert,
  biller: ShieldQuestion,
};

export default function StaffUsersTms() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [credential, setCredential] = useState(null); // { email, temp_password }

  const load = () => api.get("/admin/staff").then(r => setRows(r.data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete staff user "${name}"? They will lose access immediately.`)) return;
    try { await api.delete(`/admin/staff/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const filtered = rows.filter(r => !q || (r.name + r.email + r.role).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="tms-staff-page">
      <div className="flex items-center justify-between">
        <h1 style={{ fontFamily: "'Cabinet Grotesk', 'Barlow', sans-serif" }} className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-950">Staff Users</h1>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search staff…" className={"pl-8 " + inputCls} data-testid="staff-search" /></div>
          <button onClick={() => setCreating(true)} className={btnPri} data-testid="add-staff-btn">
            <Plus className="h-3.5 w-3.5" /> Add Staff User
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RoleCard role="admin" desc="Full access — jobs, billing, users, everything." count={rows.filter(r => r.role === "admin").length} />
        <RoleCard role="dispatcher" desc="Create/edit jobs, dispatch loads, send Rate Confirmations." count={rows.filter(r => r.role === "dispatcher").length} />
        <RoleCard role="biller" desc="Generate invoices & BOLs, mark ready-to-invoice, upload PODs." count={rows.filter(r => r.role === "biller").length} />
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-200">
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-left px-4 py-3">Created</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((r, i) => {
              const Icon = roleIcons[r.role];
              return (
                <tr key={r.user_id} className={i % 2 ? "bg-zinc-50" : "bg-white"} data-testid={`staff-${r.user_id}`}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{r.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 inline-flex items-center gap-1 ${roleColorCls[r.role] || "bg-zinc-700 text-white"}`}>
                      {Icon && <Icon className="h-3 w-3" />} {ROLE_LABEL[r.role] || r.role}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{r.phone || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{r.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => del(r.user_id, r.name)} className="text-xs text-red-600" data-testid={`del-staff-${r.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={6} className="p-10 text-center text-sm text-zinc-500">No staff users yet. Add your first team member.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateStaffModal
          onClose={() => setCreating(false)}
          onCreated={(cred) => { setCreating(false); setCredential(cred); load(); }}
        />
      )}
      {credential && <CredentialModal cred={credential} onClose={() => setCredential(null)} />}
    </div>
  );
}

function RoleCard({ role, desc, count }) {
  const Icon = roleIcons[role];
  return (
    <div className={`border p-4 ${roleColorCls[role]} bg-opacity-10`} style={{ borderColor: "currentColor" }}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 ${roleColorCls[role]} inline-flex items-center gap-1`}>
          {Icon && <Icon className="h-3 w-3" />} {ROLE_LABEL[role]}
        </span>
        <span className="font-mono text-2xl font-bold text-zinc-950">{count}</span>
      </div>
      <p className="mt-2 text-xs text-zinc-700 leading-relaxed">{desc}</p>
    </div>
  );
}

function CreateStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "dispatcher", phone: "", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      toast.success(`Created ${form.role} account`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white border border-zinc-950 max-w-2xl w-full my-8">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div style={{ fontFamily: "'Cabinet Grotesk', 'Barlow', sans-serif" }} className="font-bold text-lg text-zinc-950">Create Staff User</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950 text-xl">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Full Name *</label><input className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="staff-form-name" /></div>
            <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="staff-form-email" /></div>
            <div>
              <label className={labelCls}>Role *</label>
              <select className={inputCls} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="staff-form-role">
                <option value="admin">Admin — full access</option>
                <option value="dispatcher">Dispatcher — jobs & rate confirmations</option>
                <option value="biller">Biller — invoices & payments</option>
              </select>
            </div>
            <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2">
              <label className={labelCls}>Temporary Password (optional — auto-generated if empty)</label>
              <input className={inputCls} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Leave blank for secure auto-generated password" data-testid="staff-form-password" />
            </div>
          </div>
          <div className="text-xs text-zinc-500 border-t border-zinc-200 pt-3">
            The new user will receive their credentials by email (once the SMTP integration is enabled). Until then, the temporary password will be shown once on the next screen — copy it and share it manually.
          </div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className={btnSec}>Cancel</button>
          <button onClick={submit} disabled={saving} className={btnPri} data-testid="staff-form-submit">
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CredentialModal({ cred, onClose }) {
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white border-2 border-emerald-600 max-w-lg w-full">
        <div className="px-5 py-4 border-b border-zinc-200 bg-emerald-50">
          <div style={{ fontFamily: "'Cabinet Grotesk', 'Barlow', sans-serif" }} className="font-bold text-lg text-emerald-900">Account Created</div>
          <div className="text-xs text-emerald-700 mt-1">Share these credentials securely. The password will not be shown again.</div>
        </div>
        <div className="p-5 space-y-3">
          <Cred label="Email" value={cred.email} onCopy={copy} />
          <Cred label="Temporary Password" value={cred.temp_password} onCopy={copy} />
          <Cred label="Role" value={cred.role} onCopy={copy} />
          <div className="text-[11px] text-zinc-500 mt-4">
            {cred.email_sent ? "✓ Email sent." : "Email delivery is MOCKED — enable the SMTP integration to send automatic emails."}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex justify-end">
          <button onClick={onClose} className={btnPri} data-testid="cred-close-btn">I've saved these — close</button>
        </div>
      </div>
    </div>
  );
}

function Cred({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2 border border-zinc-200 p-2 bg-zinc-50">
      <div className="flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">{label}</div>
        <div className="font-mono text-sm text-zinc-950 select-all">{value}</div>
      </div>
      <button onClick={() => onCopy(value)} className={btnSec} data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <Copy className="h-3 w-3" /> Copy
      </button>
    </div>
  );
}
